import { apiClient } from "./apiClient";

/**
 * Client for Atlas's WhatsApp number replacement API
 * (`WhatsAppNumberReplacementController`).
 *
 * Base path (from the controller's `@RequestMapping`):
 *   /api/ui/v1/communication-channels/{channelId}/whatsapp/replace-number
 *
 * `apiClient` already prefixes `/api/ui/v1`, so paths here are relative to it.
 *
 * Every endpoint is `@PreAuthorize("hasAuthority('communication_channel:manage')")`
 * plus a per-channel scope check inside the service — the UI gates on the same
 * permission name via <Can permission="communication_channel:manage">, it does
 * NOT re-implement the rule.
 */

/**
 * `ReplacementStatus` — copied verbatim from
 * `com.bridgeai.atlas.core.entity.whatsappreplacement.ReplacementStatus`.
 * Do not add UI-only members: the UI must never invent a state the backend
 * cannot report.
 */
export const REPLACEMENT_STATUSES = [
  "REQUESTED",
  "VERIFYING",
  "VERIFIED",
  "CUTOVER_IN_PROGRESS",
  "CHAT_GATEWAY_PENDING",
  "CHATWOOT_DISPLAY_PENDING",
  "VERIFYING_NEW_PATH",
  "COMPLETED",
  "VERIFICATION_FAILED",
  "CUTOVER_FAILED",
  "ROLLBACK_IN_PROGRESS",
  "ROLLED_BACK",
  "ROLLBACK_FAILED",
] as const;

export type ReplacementStatus = (typeof REPLACEMENT_STATUSES)[number];

/**
 * `ReplacementStage` — copied verbatim from the backend enum. Forward-only and
 * monotonic on the backend; the UI treats it as a progress marker only and
 * never derives success from it.
 */
export const REPLACEMENT_STAGES = [
  "NONE",
  "VERIFICATION_PASSED",
  "CHAT_GATEWAY_PREPARED",
  "TRAFFIC_CUTOVER_APPLIED",
  "CHAT_GATEWAY_FINALIZED",
  "NEW_PATH_VERIFIED",
  "CHANNEL_PROMOTED",
] as const;

export type ReplacementStage = (typeof REPLACEMENT_STAGES)[number];

/** `ReplacementFailureCode` — copied verbatim from the backend enum. */
export const REPLACEMENT_FAILURE_CODES = [
  "CHANNEL_NOT_FOUND",
  "UNAUTHORIZED_FOR_CHANNEL",
  "INVALID_REQUEST",
  "REPLACEMENT_ALREADY_IN_PROGRESS",
  "CHANNEL_MISSING_CURRENT_WHATSAPP_CONFIG",
  "TOKEN_INVALID_FOR_WABA",
  "PHONE_NUMBER_WABA_MISMATCH",
  "DISPLAY_NUMBER_MISMATCH",
  "DUPLICATE_PHONE_NUMBER",
  "SAME_NUMBER_AS_CURRENT",
  "CROSS_WABA_REPLACEMENT_NOT_ENABLED",
  "WA_GATEWAY_BRANCH_NOT_FOUND",
  "WA_GATEWAY_CUTOVER_FAILED",
  "CHAT_GATEWAY_PREPARE_FAILED",
  "CHAT_GATEWAY_FINALIZE_FAILED",
  "NEW_PATH_VERIFICATION_FAILED",
  "PROMOTION_FAILED",
  "ROLLBACK_WA_GATEWAY_FAILED",
  "ROLLBACK_CHAT_GATEWAY_FAILED",
  "NO_CHATWOOT_DISPLAY_RETRY_PENDING",
  "NO_ROLLBACK_RETRY_PENDING",
  "INTERNAL_ERROR",
] as const;

export type ReplacementFailureCode = (typeof REPLACEMENT_FAILURE_CODES)[number];

/**
 * Body of `POST .../replace-number` — mirrors `ReplaceNumberRequest`.
 * All four value fields are `@NotBlank` on the backend; `idempotencyKey` is
 * optional and defaults server-side to `"auto:" + newPhoneNumberId`.
 *
 * `newAccessToken` is held in memory only. It is never persisted by this
 * module, never logged, and never placed in a query string.
 */
export interface ReplaceNumberRequest {
  /** E.164, `^\+[1-9]\d{1,14}$` — the backend rejects anything else. */
  newPhone: string;
  newPhoneNumberId: string;
  newWabaId: string;
  newAccessToken: string;
  idempotencyKey?: string;
}

/**
 * Response of all three endpoints — mirrors `ReplaceNumberStatusResponse`.
 *
 * Deliberately has no token field: the backend assembles this field-by-field
 * precisely so no credential can reach the wire.
 *
 * `status` is typed as `ReplacementStatus | string` on purpose. A backend that
 * gains a new enum member must not be forced through a TS union that would
 * make an unknown value look like a known one — see `describeReplacement`,
 * which fails safe on anything it does not recognise.
 */
export interface ReplaceNumberStatusResponse {
  replacementId?: string | null;
  channelId?: string | null;
  status?: ReplacementStatus | string | null;
  stage?: ReplacementStage | string | null;
  oldPhone?: string | null;
  oldPhoneNumberId?: string | null;
  oldWabaId?: string | null;
  newPhone?: string | null;
  newPhoneNumberId?: string | null;
  newWabaId?: string | null;
  initiatedBy?: string | null;
  startedAt?: string | null;
  updatedAt?: string | null;
  completedAt?: string | null;
  /** When wa-gateway began routing the new number. Null until the pivot. */
  trafficCutoverAt?: string | null;
  /** Backend-computed `status.trafficMayBeOnNewNumber()`. */
  trafficOnNewNumber?: boolean | null;
  failureCode?: ReplacementFailureCode | string | null;
  failureMessage?: string | null;
}

function basePath(channelId: string): string {
  return `/communication-channels/${encodeURIComponent(channelId)}/whatsapp/replace-number`;
}

export const whatsappNumberReplacementService = {
  /**
   * `POST .../replace-number`.
   *
   * Returns 202 while in flight, 200 for COMPLETED / CHATWOOT_DISPLAY_PENDING,
   * 4xx/5xx for rejections and failures — all with the same body shape. A
   * non-2xx therefore still carries a real status, but axios rejects it, so
   * callers must treat a thrown error as "unknown outcome" and re-read
   * {@link getStatus} rather than assuming failure.
   */
  start: (channelId: string, body: ReplaceNumberRequest): Promise<ReplaceNumberStatusResponse> =>
    apiClient.post<ReplaceNumberStatusResponse>(basePath(channelId), body).then((r) => r.data),

  /**
   * `GET .../replace-number/status`.
   *
   * Backed by `findFirstByChannelIdOrderByStartedAtDesc` — the LATEST
   * replacement for the channel, terminal or not. 204 No Content when the
   * channel has never had one, which this maps to `null`.
   *
   * This is the sole authority for replacement state. Nothing about an
   * in-flight replacement is kept in browser storage.
   */
  getStatus: async (channelId: string): Promise<ReplaceNumberStatusResponse | null> => {
    const res = await apiClient.get<ReplaceNumberStatusResponse | "">(`${basePath(channelId)}/status`);
    if (res.status === 204 || !res.data) return null;
    return res.data as ReplaceNumberStatusResponse;
  },

  /**
   * `POST .../replace-number/retry-chatwoot-display`.
   *
   * Only valid while the latest replacement is CHATWOOT_DISPLAY_PENDING —
   * otherwise 404 `NO_CHATWOOT_DISPLAY_RETRY_PENDING`. Takes no body and
   * needs no Meta token: promotion already ran, so chat-gateway's FINALIZE is
   * the only outstanding call and it needs no credentials.
   */
  retryChatwootDisplay: (channelId: string): Promise<ReplaceNumberStatusResponse> =>
    apiClient
      .post<ReplaceNumberStatusResponse>(`${basePath(channelId)}/retry-chatwoot-display`)
      .then((r) => r.data),

  /**
   * `POST .../replace-number/retry-rollback`.
   *
   * Resumes a rollback parked in ROLLBACK_FAILED — it re-runs the compensating
   * operations that have not yet succeeded (restoring routing on
   * bridge-whatsapp-gateway and the mapping on bridge-chat-gateway) and can
   * only reach ROLLED_BACK once each of them is proven complete.
   *
   * **This is not a status reset.** There is deliberately no force-complete /
   * mark-rolled-back / clear-failure endpoint on the backend, and this module
   * must never grow one: ROLLBACK_FAILED can describe a partly compensated
   * distributed operation, so flipping the status without finishing the
   * compensations would make Atlas claim the old number is restored while
   * downstream routing still disagreed.
   *
   * Only valid while the latest replacement is ROLLBACK_FAILED — otherwise 404
   * `NO_ROLLBACK_RETRY_PENDING`. Takes no body and needs no Meta token: the
   * previous number's token is still on the channel record (promotion runs
   * last, so a rollback-able replacement never overwrote it).
   *
   * Answers 202 while the resumed rollback is still running, and a non-2xx —
   * carrying a real status body — when it settles in ROLLED_BACK or fails
   * again. As with `start`, a thrown error means "unknown outcome, re-read
   * {@link getStatus}", never "the rollback failed".
   */
  retryRollback: (channelId: string): Promise<ReplaceNumberStatusResponse> =>
    apiClient
      .post<ReplaceNumberStatusResponse>(`${basePath(channelId)}/retry-rollback`)
      .then((r) => r.data),
};
